import { getOrgAgentsByAgentIdBuilder, postOrgAgentsBuilderMutation } from '@onefootprint/fetch-dashboard';
import type { AgentBuilderResponse, CreateAgentBuilderRequest } from '@onefootprint/request-types/dashboard';
import { useMutation } from '@tanstack/react-query';
import { type NextRouter, useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stashCreatedBuilder, takeStashedBuilder } from '../store/builder-create-stash';
import { useBuilder } from '../store/provider';
const useBuilderBootstrap = (): {
  error: unknown;
} => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder'
  });
  const {
    state,
    dispatch,
    canEdit
  } = useBuilder();
  const router = useRouter();
  const createSession = useMutation(postOrgAgentsBuilderMutation());
  const [bootstrapError, setBootstrapError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const applyResponse = (response: AgentBuilderResponse) => {
    dispatch({
      type: 'bootstrapFromApi',
      response
    });
  };
  const loadExisting = async (agentId: string) => {
    const stashed = takeStashedBuilder(agentId);
    if (stashed) {
      applyResponse(stashed);
      return;
    }
    setIsLoading(true);
    try {
      const {
        data
      } = await getOrgAgentsByAgentIdBuilder({
        path: {
          agentId
        },
        throwOnError: true
      });
      if (data) applyResponse(data);
    } catch (e) {
      setBootstrapError(e);
    } finally {
      setIsLoading(false);
    }
  };
  const createAndRedirect = () => {
    createSession.mutate({
      body: {
        template: getTemplate(router.query)
      }
    }, {
      onSuccess: response_0 => {
        applyResponse(response_0);
        stashCreatedBuilder(response_0);
        router.replace({
          pathname: '/agents/[id]/builder',
          query: {
            id: response_0.agentId
          }
        }, undefined, {
          shallow: true
        });
      }
    });
  };

  // `isError` is in the guard so a single failure doesn't loop. The caller
  // surfaces `error` inline so the user can read it without a stack of toasts.
  useEffect(() => {
    if (!router.isReady || state.session || isLoading || createSession.isPending || createSession.isError || bootstrapError) {
      return;
    }
    const agentId_0 = getAgentId(router.query);
    if (agentId_0) {
      void loadExisting(agentId_0);
    } else if (canEdit) {
      createAndRedirect();
    } else {
      setBootstrapError(new Error(t($ => $.canvas.header.noEditPermission)));
    }
  }, [router.isReady, state.session, isLoading, createSession.isPending, createSession.isError, bootstrapError, canEdit]);
  return {
    error: createSession.error ?? bootstrapError
  };
};
const getAgentId = (query: NextRouter['query']): string | undefined => {
  const raw = query.id;
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
};
type TemplateKind = CreateAgentBuilderRequest['template'];
const getTemplate = (query: NextRouter['query']): TemplateKind => {
  const raw = query.template;
  return typeof raw === 'string' && raw.length > 0 ? raw as TemplateKind : 'custom';
};
export default useBuilderBootstrap;